import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ShoppingBag, Minus, Plus, Disc3, Download, CreditCard, Landmark,
  CheckCircle2, Music, Mic, PenLine, CalendarDays, X, Copy, ChevronDown
} from 'lucide-react'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '../firebase/config'
import { toast } from '../hooks/useToast'
import { vibrate } from '../utils/appPrefs'
import './BoutiquePage.css'

/* =====================================================================
   BOUTIQUE — Précommande du CD « Hommage à Johnny, Juste une chanson… »

   Copie fidèle de la page du site (cd-johnny.html) :
   - mêmes formats et prix,
   - mêmes moyens de paiement (carte Stripe, PayPal, virement),
   - MÊME DOCUMENT enregistré dans la MÊME collection :
       100johnny / main / CommandesCD
     Les commandes du site et de l'app arrivent au même endroit,
     avec les mêmes champs. Seul ajout : source = 'app'.
   ===================================================================== */

const FIRESTORE_PATH = ['100johnny', 'main', 'CommandesCD']
const CLOUD_FUNCTIONS_BASE = 'https://us-central1-pour100johnny-app.cloudfunctions.net/api'
const STRIPE_PUBLISHABLE_KEY =
  import.meta.env.VITE_STRIPE_PUBLIC_KEY ||
  'pk_live_51LPiiPCAM7wuySpjWwTZD3h8rLoMYh8ttO7LBMtMfLISCfemm5SkNqM3SKkS1FU1z6KSEV3Gap7w1qMBt5yzP3B700cQgSAgFL'
const PAYPAL_CLIENT_ID = 'AX4yXJ9gGOEKa4_LESrCYxZ_zg-F-kg30CBVlKN-UCNIkqEn3c2EJdnS19Y2LvqCVkFHcZVccpv-E5xw'

const PHYSIQUE_PRICE = 12
const SHIPPING_PRICE = 3
const NUMERIQUE_PRICE = 8
const RELEASE_DATE = '2026-12-05'
const CONTACT_EMAIL = 'contact@100pour100johnny.com'

const BANK = {
  beneficiary: 'Radio 100% Johnny',
  iban: 'FR76 2823 3000 0140 2117 7471 871',
  bic: 'REVOFRP2'
}

const IMG = (name) => `https://100pour100johnny.com/images/${name}`
const GALLERY = [
  { src: IMG('Avant.png'), label: 'Jaquette' },
  { src: IMG('Dos.png'), label: 'Verso' },
  { src: IMG('CD.png'), label: 'Le disque' },
  { src: IMG('Livret_1.png'), label: 'Livret p.1' },
  { src: IMG('Livret_2.png'), label: 'Livret p.2' },
  { src: IMG('Livret_3.png'), label: 'Livret p.3' },
  { src: IMG('Livret_4.png'), label: 'Livret p.4' }
]

const TRACKS = [
  ['01', 'Johnny reviens juste une chanson', '4:24'],
  ['02', 'Après le dernier rideau', '5:39'],
  ['03', 'Dans le cœur des motards', '5:08'],
  ['04', 'Quand les guitares se taisent', '5:29'],
  ['05', 'Celui qui lui montra le rock', '3:44'],
  ['06', "Johnny, c'était nous deux", '4:31'],
  ['07', 'Johnny sur la Route 66', '5:27'],
  ['08', 'Johnny, je te réponds en blues', '5:06'],
  ['09', 'Sur la route avec Johnny', '4:01'],
  ['10', 'Ton absence pèse lourd', '5:38'],
  ['11', 'Le Cinq Décembre', '4:53'],
  ['12', "Bien plus qu'un prénom", '6:09'],
  ['13', 'Depuis mes quatre ans', '4:28'],
  ['14', 'Hymne 100% Johnny', '4:30'],
  ['15', 'Entre toi et nous', '4:51', 'Duo avec Ruby']
]

// Même format de prix que le site : "15€", "12,50€"
const formatPrice = (n) => `${Number(n).toFixed(2).replace('.', ',').replace(',00', '')}€`
const unitPrice = (format) => (format === 'physique' ? PHYSIQUE_PRICE + SHIPPING_PRICE : NUMERIQUE_PRICE)
const generateReference = () => `CD-${Math.random().toString(36).substring(2, 8).toUpperCase()}`

const loadScript = (src) => new Promise((resolve, reject) => {
  if (document.querySelector(`script[src="${src}"]`)) {
    if (src.includes('stripe') ? window.Stripe : window.paypal) { resolve(); return }
  }
  const s = document.createElement('script')
  s.src = src
  s.async = true
  s.onload = resolve
  s.onerror = reject
  document.head.appendChild(s)
})

// ---------------------------------------------------------------------
// Petits éléments
// ---------------------------------------------------------------------
function Stepper({ value, onChange, min = 0, max = 20, label }) {
  return (
    <div className="bq-qty" role="group" aria-label={label}>
      <button type="button" onClick={() => { vibrate(8); onChange(Math.max(min, value - 1)) }} disabled={value <= min} aria-label="Moins">
        <Minus size={20} />
      </button>
      <span aria-live="polite">{value}</span>
      <button type="button" onClick={() => { vibrate(8); onChange(Math.min(max, value + 1)) }} disabled={value >= max} aria-label="Plus">
        <Plus size={20} />
      </button>
    </div>
  )
}

function Field({ label, wide, children }) {
  return (
    <label className={`bq-field ${wide ? 'bq-field--wide' : ''}`}>
      <span>{label}</span>
      {children}
    </label>
  )
}

// ---------------------------------------------------------------------
// Carte bancaire (Stripe) — même appel que le site
// ---------------------------------------------------------------------
function StripeCard({ onReady }) {
  const mountRef = useRef(null)
  const [status, setStatus] = useState('loading')

  useEffect(() => {
    let card = null
    let destroyed = false
    loadScript('https://js.stripe.com/v3/')
      .then(() => {
        if (destroyed || !mountRef.current) return
        const stripe = window.Stripe(STRIPE_PUBLISHABLE_KEY)
        card = stripe.elements().create('card', {
          hidePostalCode: true,
          style: {
            base: { color: '#f5f1e8', fontSize: '16px', iconColor: '#d4af37', '::placeholder': { color: '#8a8171' } },
            invalid: { color: '#ff8a95' }
          }
        })
        card.mount(mountRef.current)
        card.on('ready', () => !destroyed && setStatus('ready'))
        onReady({ stripe, card })
      })
      .catch(() => { if (!destroyed) setStatus('error') })
    return () => { destroyed = true; try { card?.destroy() } catch { /* rien */ } }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div ref={mountRef} className="bq-card-input" />
      {status === 'loading' && <p className="bq-muted">Chargement du paiement sécurisé…</p>}
      {status === 'error' && <p className="bq-alert">Impossible de charger le paiement par carte. Vérifiez votre connexion.</p>}
      <p className="bq-muted bq-small">Paiement sécurisé par Stripe. Vos données bancaires ne passent pas par nos serveurs.</p>
    </div>
  )
}

// ---------------------------------------------------------------------
// PayPal — même commande que le site
// ---------------------------------------------------------------------
function PayPalBox({ getTotal, validate, onPaid }) {
  const boxRef = useRef(null)
  const [status, setStatus] = useState('loading')
  const latest = useRef({ getTotal, validate, onPaid })
  latest.current = { getTotal, validate, onPaid }

  useEffect(() => {
    let destroyed = false
    loadScript(`https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=EUR&locale=fr_FR`)
      .then(() => {
        if (destroyed || !boxRef.current || !window.paypal) return
        boxRef.current.innerHTML = ''
        window.paypal.Buttons({
          style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal', height: 50 },
          onClick: (data, actions) => (latest.current.validate() ? actions.resolve() : actions.reject()),
          createOrder: (data, actions) => actions.order.create({
            purchase_units: [{ amount: { value: latest.current.getTotal().toFixed(2), currency_code: 'EUR' } }]
          }),
          onApprove: async (data, actions) => {
            const details = await actions.order.capture()
            await latest.current.onPaid({ paypalOrderId: details.id })
          },
          onError: (err) => {
            console.error('Erreur PayPal:', err)
            toast.error("Le paiement PayPal n'a pas pu être traité. Réessayez ou choisissez un autre moyen de paiement.")
          }
        }).render(boxRef.current)
        setStatus('ready')
      })
      .catch(() => { if (!destroyed) setStatus('error') })
    return () => { destroyed = true }
  }, [])

  return (
    <div>
      {status === 'loading' && <p className="bq-muted">Chargement de PayPal…</p>}
      {status === 'error' && (
        <p className="bq-alert">PayPal n'est pas disponible pour le moment. Choisissez « Carte » ou « Virement ».</p>
      )}
      <div ref={boxRef} />
    </div>
  )
}

// =====================================================================
// PAGE
// =====================================================================
export default function BoutiquePage({ currentUser }) {
  const navigate = useNavigate()
  const orderRef = useRef(null)
  const stripeRef = useRef(null)

  const [qPhys, setQPhys] = useState(1)
  const [qNum, setQNum] = useState(0)
  const [contact, setContact] = useState(() => ({
    firstName: (currentUser?.displayName || '').split(' ')[0] || '',
    lastName: (currentUser?.displayName || '').split(' ').slice(1).join(' ') || '',
    email: currentUser?.email || '',
    phone: '',
    address: '', address2: '', zip: '', city: '', country: 'France'
  }))
  const [method, setMethod] = useState('stripe') // stripe | paypal | virement
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [confirmed, setConfirmed] = useState(null)
  const [lightbox, setLightbox] = useState(null)
  const [showTracks, setShowTracks] = useState(false)
  const [nearOrder, setNearOrder] = useState(false)

  // La barre « Précommander » disparaît quand on arrive au formulaire
  useEffect(() => {
    const check = () => {
      const el = orderRef.current
      if (el) setNearOrder(el.getBoundingClientRect().top < window.innerHeight * 0.85)
    }
    check()
    window.addEventListener('scroll', check, { capture: true, passive: true })
    window.addEventListener('resize', check)
    return () => {
      window.removeEventListener('scroll', check, { capture: true })
      window.removeEventListener('resize', check)
    }
  }, [])

  // Panier au même format que le site : { key, format, qty, unit, total }
  const cart = useMemo(() => {
    const lines = []
    if (qPhys > 0) lines.push({ key: 'line-1', format: 'physique', qty: qPhys, unit: unitPrice('physique'), total: unitPrice('physique') * qPhys })
    if (qNum > 0) lines.push({ key: `line-${lines.length + 1}`, format: 'numerique', qty: qNum, unit: unitPrice('numerique'), total: unitPrice('numerique') * qNum })
    return lines
  }, [qPhys, qNum])

  const total = cart.reduce((s, l) => s + l.total, 0)
  const hasPhysical = qPhys > 0
  const set = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }))
  const clean = () => Object.fromEntries(Object.entries(contact).map(([k, v]) => [k, String(v).trim()]))

  // Mêmes contrôles que le site
  const validate = () => {
    setError('')
    const c = clean()
    if (!cart.length) { setError('Ajoutez au moins un CD (physique ou numérique).'); return false }
    if (!c.firstName || !c.lastName || !c.email || !c.phone) { setError('Merci de renseigner vos prénom, nom, email et téléphone.'); return false }
    if (!/^\S+@\S+\.\S+$/.test(c.email)) { setError('Adresse e-mail invalide.'); return false }
    if (hasPhysical && (!c.address || !c.zip || !c.city)) { setError("Merci de renseigner l'adresse de livraison pour votre CD physique."); return false }
    return true
  }

  // Enregistrement : MÊME document que cd-johnny.html
  const submitOrder = async (paymentMethod, extra) => {
    const c = clean()
    const order = {
      firstName: c.firstName,
      lastName: c.lastName,
      email: c.email,
      phone: c.phone,
      shippingAddress: { address: c.address, address2: c.address2, zip: c.zip, city: c.city, country: c.country },
      cart,
      total,
      paymentMethod,
      status: paymentMethod === 'virement' ? 'pending_payment' : 'paid',
      paymentReference: paymentMethod === 'virement' ? generateReference() : null,
      releaseDate: RELEASE_DATE,
      createdAt: serverTimestamp(),
      source: 'app',
      userId: currentUser?.uid || null,
      ...extra
    }
    try {
      await addDoc(collection(db, ...FIRESTORE_PATH), order)
      vibrate([60, 40, 60])
      setConfirmed(order)
      setTimeout(() => orderRef.current?.scrollIntoView({ block: 'start' }), 50)
    } catch (e) {
      console.error("Erreur Firestore lors de l'enregistrement de la commande:", e)
      const code = e?.code ? ` (${e.code})` : ''
      setError(paymentMethod === 'virement'
        ? `Votre précommande n'a pas pu être enregistrée${code}. Réessayez, ou contactez-nous à ${CONTACT_EMAIL}.`
        : `Votre paiement a bien été pris en compte, mais l'enregistrement de la commande a échoué${code}. Contactez-nous à ${CONTACT_EMAIL} avec votre reçu.`)
    }
  }

  const itemsSummary = cart.map((l) => `${l.qty}x ${l.format === 'physique' ? 'CD physique' : 'Version numerique'}`).join(' + ')

  const payByCard = async () => {
    const s = stripeRef.current
    if (!s) { setError('Le paiement par carte est encore en cours de chargement.'); return }
    setBusy(true)
    try {
      const c = clean()
      const shippingSummary = hasPhysical
        ? [c.address, c.address2, `${c.zip} ${c.city}`, c.country].filter(Boolean).join(', ')
        : 'Aucune (numerique)'
      const resp = await fetch(`${CLOUD_FUNCTIONS_BASE}/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: total,
          currency: 'eur',
          description: `CD Hommage a Johnny - Precommande (${itemsSummary})`,
          metadata: {
            order_type: 'CD_HOMMAGE_JOHNNY',
            items: itemsSummary,
            has_physical: hasPhysical ? 'oui' : 'non',
            shipping_address: shippingSummary,
            release_date: RELEASE_DATE,
            phone: c.phone,
            source: 'app'
          },
          customer_data: { name: `${c.firstName} ${c.lastName}`, email: c.email, phone: c.phone }
        })
      })
      const data = await resp.json()
      if (!data.success) throw new Error(data.error || 'Erreur lors de la création du paiement')

      const result = await s.stripe.confirmCardPayment(data.client_secret, {
        payment_method: {
          card: s.card,
          billing_details: { name: `${c.firstName} ${c.lastName}`, email: c.email, phone: c.phone }
        }
      })
      if (result.error) setError(result.error.message)
      else if (result.paymentIntent?.status === 'succeeded') {
        await submitOrder('stripe', { stripePaymentId: result.paymentIntent.id })
      }
    } catch (err) {
      console.error('Erreur paiement Stripe:', err)
      setError(`Le paiement n'a pas pu être traité (${err?.message || 'erreur inconnue'}). Vérifiez vos informations et réessayez.`)
    } finally {
      setBusy(false)
    }
  }

  const onSubmit = async () => {
    if (!validate()) return
    if (method === 'virement') {
      setBusy(true)
      await submitOrder('virement', {})
      setBusy(false)
    } else if (method === 'stripe') {
      await payByCard()
    }
  }

  const goOrder = () => {
    vibrate(10)
    orderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(() => toast.success('Copié')).catch(() => {})
  }

  // Fermer l'aperçu photo avec Échap / retour
  useEffect(() => {
    if (!lightbox) return undefined
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lightbox])

  // ---------------------------------------------------------------
  return (
    <div className="page active bq">
      <div className="bq-wrap">

        <header className="bq-head">
          <span className="bq-head__icon"><ShoppingBag size={22} /></span>
          <div>
            <h1>Boutique</h1>
            <p>Chaque achat aide la radio 100% Johnny à continuer</p>
          </div>
        </header>

        {/* ---------- Présentation ---------- */}
        <section className="bq-hero">
          <div className="bq-hero__cover">
            <span className="bq-hero__badge">Précommande ouverte</span>
            <button type="button" onClick={() => setLightbox(GALLERY[0])} aria-label="Agrandir la jaquette">
              <img src={GALLERY[0].src} alt="Hommage à Johnny, Juste une chanson : jaquette du CD" />
            </button>
          </div>

          <p className="bq-eyebrow"><Mic size={16} /> Radio 100% Johnny présente</p>
          <h2 className="bq-title">Hommage à Johnny <span>« Juste une chanson… »</span></h2>
          <p className="bq-lead">
            15 titres inédits, créés avec passion par Radio 100% Johnny, interprétés par NASH (voix IA),
            pour que la flamme continue de briller.
          </p>

          <div className="bq-facts">
            <span><Disc3 size={16} /> 15 titres</span>
            <span><Mic size={16} /> NASH (voix IA)</span>
            <span><PenLine size={16} /> Paroles : David Porcher</span>
            <span><CalendarDays size={16} /> Sortie le 05/12/2026</span>
          </div>
        </section>

        {/* ---------- Galerie ---------- */}
        <section>
          <h3 className="bq-h3">Le CD dans tous ses détails</h3>
          <div className="bq-gallery">
            {GALLERY.map((g) => (
              <button key={g.src} type="button" className="bq-gallery__item" onClick={() => setLightbox(g)}>
                <img src={g.src} alt={g.label} loading="lazy" />
                <span>{g.label}</span>
              </button>
            ))}
          </div>
        </section>

        {/* ---------- Tracklist ---------- */}
        <section className="bq-card bq-tracks">
          <button type="button" className="bq-tracks__toggle" onClick={() => setShowTracks((v) => !v)} aria-expanded={showTracks}>
            <Music size={20} /> Les 15 titres
            <ChevronDown size={20} className={showTracks ? 'is-open' : ''} />
          </button>
          {showTracks && (
            <ol className="bq-tracklist">
              {TRACKS.map(([n, name, time, note]) => (
                <li key={n}>
                  <span className="bq-tracklist__n">{n}</span>
                  <span className="bq-tracklist__name">{name}{note && <small>{note}</small>}</span>
                  <span className="bq-tracklist__time">{time}</span>
                </li>
              ))}
            </ol>
          )}
          <p className="bq-muted bq-small">
            Musique créée par l'IA, paroles de David Porcher, interprétée par NASH (voix IA).
            Hommage non officiel réalisé par la Radio 100% Johnny.
          </p>
        </section>

        {/* ================= PRÉCOMMANDE ================= */}
        <div ref={orderRef} className="bq-anchor" />

        {confirmed ? (
          <section className="bq-card bq-done">
            <CheckCircle2 size={64} />
            <h2>{confirmed.paymentMethod === 'virement' ? 'Précommande enregistrée' : 'Précommande confirmée'}</h2>
            {confirmed.paymentMethod === 'virement' ? (
              <>
                <p>
                  Merci {confirmed.firstName} ! Votre précommande de {formatPrice(confirmed.total)} est enregistrée.
                  Effectuez le virement avec la référence ci-dessous. Le CD sortira le 5 décembre 2026.
                </p>
                <button type="button" className="bq-ref" onClick={() => copy(confirmed.paymentReference)}>
                  Référence : <strong>{confirmed.paymentReference}</strong> <Copy size={16} />
                </button>
                <div className="bq-bank">
                  <div><span>Bénéficiaire</span><strong>{BANK.beneficiary}</strong></div>
                  <div><span>IBAN</span><button type="button" onClick={() => copy(BANK.iban.replace(/\s/g, ''))}>{BANK.iban} <Copy size={14} /></button></div>
                  <div><span>BIC</span><button type="button" onClick={() => copy(BANK.bic)}>{BANK.bic} <Copy size={14} /></button></div>
                </div>
                <p className="bq-warn">Indiquez impérativement la référence <strong>{confirmed.paymentReference}</strong> dans le libellé du virement.</p>
              </>
            ) : (
              <p>
                Merci {confirmed.firstName} ! Votre précommande de {formatPrice(confirmed.total)} est confirmée.
                Rendez-vous le 5 décembre 2026 pour la sortie !
              </p>
            )}
            <button type="button" className="bq-btn bq-btn--gold bq-btn--full" onClick={() => navigate('/')}>
              Retour à la radio
            </button>
          </section>
        ) : (
          <>
            {/* 1. Format */}
            <section className="bq-card">
              <h2 className="bq-step"><b>1</b> Choisissez votre format</h2>

              <div className={`bq-format ${qPhys > 0 ? 'is-on' : ''}`}>
                <div className="bq-format__icon"><Disc3 size={26} /></div>
                <div className="bq-format__text">
                  <strong>CD physique</strong>
                  <span className="bq-format__price">{formatPrice(unitPrice('physique'))}</span>
                  <small>12€ + 3€ de frais de port. Envoi à partir du 5 décembre 2026.</small>
                </div>
                <Stepper value={qPhys} onChange={setQPhys} label="Quantité de CD physiques" />
              </div>

              <div className={`bq-format ${qNum > 0 ? 'is-on' : ''}`}>
                <div className="bq-format__icon"><Download size={26} /></div>
                <div className="bq-format__text">
                  <strong>Version numérique</strong>
                  <span className="bq-format__price">{formatPrice(unitPrice('numerique'))}</span>
                  <small>Lien de téléchargement envoyé par e-mail à la sortie, le 5 décembre.</small>
                </div>
                <Stepper value={qNum} onChange={setQNum} label="Quantité de versions numériques" />
              </div>
            </section>

            {/* 2. Coordonnées */}
            <section className="bq-card">
              <h2 className="bq-step"><b>2</b> Vos coordonnées</h2>
              <div className="bq-form">
                <Field label="Prénom"><input value={contact.firstName} onChange={set('firstName')} autoComplete="given-name" /></Field>
                <Field label="Nom"><input value={contact.lastName} onChange={set('lastName')} autoComplete="family-name" /></Field>
                <Field label="E-mail" wide><input type="email" value={contact.email} onChange={set('email')} autoComplete="email" inputMode="email" /></Field>
                <Field label="Téléphone" wide><input type="tel" value={contact.phone} onChange={set('phone')} autoComplete="tel" inputMode="tel" /></Field>
                {hasPhysical && (
                  <>
                    <Field label="Adresse" wide><input value={contact.address} onChange={set('address')} autoComplete="address-line1" /></Field>
                    <Field label="Complément d'adresse (facultatif)" wide><input value={contact.address2} onChange={set('address2')} autoComplete="address-line2" placeholder="Appartement, bâtiment, étage…" /></Field>
                    <Field label="Code postal"><input value={contact.zip} onChange={set('zip')} autoComplete="postal-code" inputMode="numeric" /></Field>
                    <Field label="Ville"><input value={contact.city} onChange={set('city')} autoComplete="address-level2" /></Field>
                    <Field label="Pays" wide><input value={contact.country} onChange={set('country')} autoComplete="country-name" /></Field>
                  </>
                )}
              </div>
            </section>

            {/* 3. Paiement */}
            <section className="bq-card">
              <h2 className="bq-step"><b>3</b> Paiement</h2>

              <div className="bq-tabs" role="tablist">
                {[
                  ['stripe', 'Carte', CreditCard],
                  ['paypal', 'PayPal', ShoppingBag],
                  ['virement', 'Virement', Landmark]
                ].map(([id, label, Icon]) => (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={method === id}
                    className={`bq-tab ${method === id ? 'is-on' : ''}`}
                    onClick={() => { vibrate(8); setMethod(id); setError('') }}
                  >
                    <Icon size={18} /> {label}
                  </button>
                ))}
              </div>

              <div className="bq-pay">
                {method === 'stripe' && <StripeCard onReady={(s) => { stripeRef.current = s }} />}
                {method === 'paypal' && (
                  <PayPalBox getTotal={() => total} validate={validate} onPaid={(extra) => submitOrder('paypal', extra)} />
                )}
                {method === 'virement' && (
                  <div className="bq-muted">
                    <p style={{ margin: 0 }}>Votre précommande est enregistrée tout de suite et confirmée dès réception du virement.</p>
                    <div className="bq-bank">
                      <div><span>Bénéficiaire</span><strong>{BANK.beneficiary}</strong></div>
                      <div><span>IBAN</span><strong>{BANK.iban}</strong></div>
                      <div><span>BIC</span><strong>{BANK.bic}</strong></div>
                    </div>
                    <p className="bq-small">La référence à indiquer dans le libellé vous sera donnée juste après validation.</p>
                  </div>
                )}
              </div>

              <div className="bq-total">
                <span>Total</span>
                <strong>{formatPrice(total)}</strong>
              </div>

              {error && <p className="bq-alert" role="alert">{error}</p>}

              {method !== 'paypal' && (
                <button type="button" className="bq-btn bq-btn--gold bq-btn--full" onClick={onSubmit} disabled={busy || !cart.length}>
                  {busy ? 'Paiement en cours…' : `Valider ma précommande, ${formatPrice(total)}`}
                </button>
              )}
            </section>

            <p className="bq-muted bq-small bq-center">
              Une question ? {CONTACT_EMAIL}
            </p>
          </>
        )}
      </div>

      {/* Barre d'accès rapide à la précommande */}
      {!confirmed && !nearOrder && (
        <div className="bq-buy">
          <div className="bq-buy__text">
            <strong>Hommage à Johnny</strong>
            <span>à partir de {formatPrice(NUMERIQUE_PRICE)}</span>
          </div>
          <button type="button" className="bq-btn bq-btn--gold" onClick={goOrder}>Précommander</button>
        </div>
      )}

      {/* Aperçu photo */}
      {lightbox && (
        <div className="bq-lightbox" role="dialog" aria-modal="true" aria-label={lightbox.label} onClick={() => setLightbox(null)}>
          <button type="button" className="bq-lightbox__close" onClick={() => setLightbox(null)} aria-label="Fermer"><X size={24} /></button>
          <img src={lightbox.src} alt={lightbox.label} onClick={(e) => e.stopPropagation()} />
          <span>{lightbox.label}</span>
        </div>
      )}
    </div>
  )
}
